import PropTypes from "prop-types";
import React from "react";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import BillingPreviewProductDetailRow from "./BillingPreviewProductDetailRow";

const hasBillDetails = (billData) =>
	Object.keys(billData.outletBillsAfterApplyingOffers || {}).length !== 0 &&
	billData.outletBillsAfterApplyingOffers.outletBillDetails?.length !== 0;

const DetailedBillingTable = ({
	billData,
	isShowBillDiscountAndCD,
	showDiscountAmtEditable,
	onItemDiscPerChange,
	onItemDiscAmtChange,
}) => {
	const billHeaderId = billData.outletBillsAfterApplyingOffers.id;

	return (
		<Table
			stickyHeader
			aria-label="product details table"
			className="outlet-invoicing-table"
		>
			<TableHead>
				<TableRow>
					<TableCell align="center" sx={{ width: "5%" }}>
						#
					</TableCell>
					<TableCell align="left" sx={{ width: "15%" }}>
						Product Name
					</TableCell>
					<TableCell align="right" sx={{ width: "10%" }}>
						Quantity
					</TableCell>
					<TableCell align="right" sx={{ width: "10%" }}>
						Tax (%)
					</TableCell>
					<TableCell align="right" sx={{ width: "10%" }}>
						Tax Amount
					</TableCell>
					<TableCell align="center" sx={{ width: "10%" }}>
						Discount (%)
					</TableCell>
					<TableCell align="center" sx={{ width: "10%" }}>
						Discount Amount
					</TableCell>
					<TableCell align="right" sx={{ width: "10%" }}>
						Taxable Amount
					</TableCell>
					<TableCell align="right" sx={{ width: "10%" }}>
						Total Amount
					</TableCell>
				</TableRow>
			</TableHead>
			{hasBillDetails(billData) &&
				billData.outletBillsAfterApplyingOffers.outletBillDetails.map(
					(billDetails, rowIndex) => (
						<BillingPreviewProductDetailRow
							key={billDetails.id || rowIndex}
							rowIndex={rowIndex}
							billHeaderId={billHeaderId}
							billDetails={billDetails}
							isShowBillDiscountAndCD={isShowBillDiscountAndCD}
							showDiscountAmtEditable={showDiscountAmtEditable}
							onItemDiscPerChange={onItemDiscPerChange}
							onItemDiscAmtChange={onItemDiscAmtChange}
						/>
					),
				)}
		</Table>
	);
};

const SimpleBillingTable = ({ billData }) => (
	<Table
		stickyHeader
		aria-label="product details table"
		className="outlet-invoicing-table"
	>
		<TableHead>
			<TableRow>
				<TableCell align="center" sx={{ width: "5%" }}>
					#
				</TableCell>
				<TableCell align="left" sx={{ width: "25%" }}>
					Product Name
				</TableCell>
				<TableCell align="right" sx={{ width: "12%" }}>
					Quantity
				</TableCell>
				<TableCell align="right" sx={{ width: "12%" }}>
					Free Quantity
				</TableCell>
				<TableCell align="right" sx={{ width: "15%" }}>
					Taxable Amount
				</TableCell>
				<TableCell align="right" sx={{ width: "15%" }}>
					Total Tax
				</TableCell>
				<TableCell align="right" sx={{ width: "15%" }}>
					Total Amount
				</TableCell>
			</TableRow>
		</TableHead>
		{hasBillDetails(billData) &&
			billData.outletBillsAfterApplyingOffers.outletBillDetails.map(
				(billDetails, rowIndex) => (
					<TableRow key={billDetails.id || rowIndex}>
						<TableCell align="center">
							<TypographyCompo>{rowIndex + 1}</TypographyCompo>
						</TableCell>
						<TableCell align="left">
							<TypographyCompo className="cell-product">
								{billDetails?.product?.name || ""}
							</TypographyCompo>
						</TableCell>
						<TableCell align="right">
							<TypographyCompo>
								{billDetails !== null ? billDetails.qty.toFixed(2) : ""}
							</TypographyCompo>
						</TableCell>
						<TableCell align="right">
							<TypographyCompo>
								{billDetails !== null ? billDetails.freeQty.toFixed(2) : ""}
							</TypographyCompo>
						</TableCell>
						<TableCell align="right">
							<TypographyCompo>
								{billDetails !== null
									? billDetails.taxableAmt.toFixed(2)
									: ""}
							</TypographyCompo>
						</TableCell>
						<TableCell align="right">
							<TypographyCompo>
								{(
									(billDetails.totalAmt === null ? 0 : billDetails.totalAmt) -
									(billDetails.taxableAmt === null ? 0 : billDetails.taxableAmt)
								).toFixed(2)}
							</TypographyCompo>
						</TableCell>
						<TableCell align="right">
							<TypographyCompo className="cell-total">
								{billDetails !== null
									? billDetails.totalAmt.toFixed(2)
									: ""}
							</TypographyCompo>
						</TableCell>
					</TableRow>
				),
			)}
	</Table>
);

/**
 * Product line-item table for AOI billing preview.
 * Not shared with OrderList — that table is a different dialog for order selection.
 */
const BillingPreviewProductTable = ({
	billData,
	isDetailedBillingView,
	isShowBillDiscountAndCD,
	showDiscountAmtEditable,
	onItemDiscPerChange,
	onItemDiscAmtChange,
}) => (
	<div className="billing-preview-table-wrapper outlet-invoicing-table-wrapper">
		<TableContainer className="outlet-invoicing-table-container">
			{isDetailedBillingView ? (
				<DetailedBillingTable
					billData={billData}
					isShowBillDiscountAndCD={isShowBillDiscountAndCD}
					showDiscountAmtEditable={showDiscountAmtEditable}
					onItemDiscPerChange={onItemDiscPerChange}
					onItemDiscAmtChange={onItemDiscAmtChange}
				/>
			) : (
				<SimpleBillingTable billData={billData} />
			)}
		</TableContainer>
	</div>
);

BillingPreviewProductTable.propTypes = {
	billData: PropTypes.object.isRequired,
	isDetailedBillingView: PropTypes.bool.isRequired,
	isShowBillDiscountAndCD: PropTypes.bool.isRequired,
	showDiscountAmtEditable: PropTypes.bool.isRequired,
	onItemDiscPerChange: PropTypes.func.isRequired,
	onItemDiscAmtChange: PropTypes.func.isRequired,
};

export default BillingPreviewProductTable;
